/* @flow */

import React from 'react';
import { observable, action } from 'mobx';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import Route from 'react-router/route';
import { FormattedMessage, defineMessages } from 'react-intl';
import Helmet from 'helmet/helmet';

import Styled from 'styled';
import { URLBuilder } from 'url-builder/url-builder';
import { URLBuilderLink } from 'lib/url/url-builder-link';
import SVGIconView from 'lib/images/svg-icon-view';
import { Halfpage, ZoznamHalfpageTop, ZoznamHalfpageBottom, ZahradaDoubleSquare, ZahradaSquare } from 'lib/google/dfp/ad-units/halfpage';

import { Site } from 'four-nets/site';
import section from 'four-nets/icons/svg/icon_all-sections.svg';
import subsection from 'four-nets/icons/svg/icon_sub-section.svg';
import SplitView from 'four-nets/ui/layout/split-view';
import TitleView from 'four-nets/ui/title-view';
import ContextMenuView, { ContextMenuViewModel, ContextMenuItem } from 'four-nets/common/context-menu-view';
import HotContentWidget from 'four-nets/cms/hot-content-widget';
import BlogSelectionView, { BlogSelectionViewModel } from 'four-nets/widgets/blog-selection-view/blog-selection-view';

import { CategoryLink } from '../category-link';
import defaultStyles from './blog-view.scss';

import { CategoryStore, PostStore } from '../blog';

import splitStyles from './split-view.scss';
import { TryNewDemanderBox } from 'four-nets/photoblog/try-new-demander';

export const LAYOUT = {
    post: 'post',
    category: 'category',
};

export class BlogViewModel {
    contextMenuViewModel: ContextMenuViewModel;
    blogSelectionViewModel: BlogSelectionViewModel;

    urlBuilder: URLBuilder;
    categoryStore: CategoryStore;
    postStore: PostStore;

    @observable activeCategory: ?Category;
    @observable layout: string;

    constructor(dataSource) {
        const { intl } = dataSource;

        this.allCategoriesMenuItem = new ContextMenuItem(
            new URLBuilderLink({
                url: 'blog',
                label: intl.formatMessage(defineMessages({ ALL_CATEGORIES: { id: 'ALL_CATEGORIES' } }).ALL_CATEGORIES),
            }),
            <SVGIconView src={subsection} />,
            <SVGIconView src={section} />
        );

        this.menuItems = [this.allCategoriesMenuItem];

        dataSource.categories.forEach(category => {
            if (category.slug !== 'clanky-od-partnerov' && category.slug !== 'clanky-od-partneru') {
                this.menuItems.push(
                    new ContextMenuItem(
                        new CategoryLink(category),
                        <SVGIconView src={subsection} />,
                        <SVGIconView src={section} />
                    )
                );
            }
        });

        this.contextMenuViewModel = new ContextMenuViewModel(this.menuItems);
        this.blogSelectionViewModel = new BlogSelectionViewModel(dataSource);
    }

    load = () => {
        return this.blogSelectionViewModel.load();
    };

    @action setLayout({ layout }: { layout: string }) {
        this.layout = layout;
    }

    fromJSON(json) {
        this.blogSelectionViewModel.fromJSON(json.blogSelectionViewModel);
    }

    toJSON() {
        return {
            blogSelectionViewModel: this.blogSelectionViewModel,
        };
    }

    @action setActiveCategory({ activeCategory }: { activeCategory: ?Category }) {
        this.activeCategory = activeCategory;
        let linkSet = false;
        for (let i = 0; i < this.menuItems.length; i++) {
            if (this.menuItems[i].link.category === this.activeCategory) {
                this.contextMenuViewModel.setActiveMenuItem(this.menuItems[i]);
                linkSet = true;
            }
        }
        if (!linkSet) {
            this.contextMenuViewModel.setActiveMenuItem(this.allCategoriesMenuItem);
        }
    }
}

@Styled(defaultStyles)
@observer
class BlogView extends React.Component {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
        origin: PropTypes.string,
    };

    static childContextTypes = {
        blogViewModel: PropTypes.instanceOf(BlogViewModel),
        categoryStore: PropTypes.instanceOf(CategoryStore),
        postStore: PropTypes.instanceOf(PostStore),
        categories: MobxPropTypes.arrayOrObservableArray,
    };

    getChildContext() {
        return {
            blogViewModel: this.props.blogViewModel,
            categoryStore: this.props.categoryStore,
            postStore: this.props.postStore,
            categories: this.props.categories,
        };
    }

    renderHelmet = H => {
        const { origin, site } = this.context;

        const { activeCategory } = this.props.blogViewModel;

        return (
            <FormattedMessage id='BLOG_LINK_LABEL'>
                {message => (
                    <Route>
                        {({ location }) => {
                            const title = `${activeCategory ? activeCategory.name : message} | ${site.name}`;

                            return (
                                <H>
                                    <title>{title}</title>,
                                    <meta property='og:title' content={title} />,
                                    <meta property='og:url' content={`${origin}/${location.pathname}`} />,
                                </H>
                            );
                        }}
                    </Route>
                )}
            </FormattedMessage>
        );
    };

    render() {
        const { blogViewModel, styles } = this.props;
        const { site } = this.context;
        const { activeCategory, layout } = blogViewModel;
        const title =
            layout === LAYOUT.category ? (
                <div className={styles.title}>
                    <Helmet render={this.renderHelmet} />
                    <TitleView
                        title={
                            <h1 className={styles.heading}>
                                {activeCategory ? activeCategory.name : <FormattedMessage id='ALL_CATEGORIES' />}
                            </h1>
                        }
                    />
                </div>
            ) : null;

        const menu =
            layout === LAYOUT.category ? (
                <div className={styles.categoryMenu}>
                    <ContextMenuView dropDown={true} contextMenuViewModel={blogViewModel.contextMenuViewModel} />
                </div>
            ) : null;

        const mainContent = (
            <div className={styles.content}>
                {title}
                {menu}
                {this.props.children}
            </div>
        );

        const classNames = [styles.sideContent];

        if (layout === LAYOUT.post) {
            classNames.push(styles.postLayout);
        }

        const sideContent = layout ? (
            <div className={classNames.join(' ')}>
                <HotContentWidget />
                <div className={styles.menu}>
                    <ContextMenuView contextMenuViewModel={blogViewModel.contextMenuViewModel} />
                </div>
                {(() => {
                    if (site.isSlovak && site.isWedding) {
                        return <ZoznamHalfpageTop />
                    }
                    if (site.isGarden) {
                        return <ZahradaSquare />
                    }

                    return <Halfpage adUnitCode='halfpage-top' />
                })()}

                <div className="mb-4">
                    <TryNewDemanderBox />
                </div>

                <BlogSelectionView blogSelectionViewModel={blogViewModel.blogSelectionViewModel} />
                <div className={styles.adSticky}>
                    {(() => {

                        if (site.isSlovak && site.isWedding) {
                            return <ZoznamHalfpageBottom />
                        }
                        if (site.isGarden) {
                            return <ZahradaDoubleSquare />
                        }

                        return <Halfpage adUnitCode='halfpage-bottom' />
                    })()}
                </div>
            </div>
        ) : null;

        return (
            <React.Fragment>
                <SplitView mainContent={mainContent} sideContent={sideContent} styles={splitStyles} />
            </React.Fragment>
        );
    }
}

export default BlogView;
